import { useCallback, useEffect, useRef, useState } from "react";
import { useDebaunce } from "utils";

export const useTableParams = ({
  service,
  paramsDefault,
  fetchOnlyOnSearch = false,
  onSuccess,
  enabled = true
}) => {
  const [status, setStatus] = useState("pending");
  const [errorMessage, setErrorMessage] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [params, setParams] = useState({
    page: 1,
    pageSize: 10,
    search: undefined,
    ...paramsDefault
  });
  const [count, setCount] = useState(0);
  const debaunceSearch = useDebaunce({ fn: handleSearch, delay: 400 });

  function handleSearch(e) {
    setParams(state => ({ ...state, page: 1, search: e.target.value?.trim() }));
  }

  const onChange = useCallback(newParams => {
    setParams(state => {
      const next = { ...state, ...newParams };
      return Object.keys(next).every(key => Object.is(next[key], state[key]))
        ? state
        : next;
    });
  }, []);
  const debaunceChange = useDebaunce({ fn: onChange, delay: 300 });
  const callbacks = useRef({ service, onSuccess });
  callbacks.current = { service, onSuccess };
  const requestRef = useRef({ sequence: 0, controller: null });
  const run = useCallback(async () => {
    if (!enabled) return;
    requestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = ++requestRef.current.sequence;
    requestRef.current.controller = controller;
    setErrorMessage("");
    if (fetchOnlyOnSearch && !params.search) {
      setStatus("success");
      setData([]);
      return;
    }
    try {
      setStatus("pending");
      const { data } = await callbacks.current.service(
        {
          ...params,
          pageIndex: params.page,
          orderBy: params.orderDirection
        },
        { signal: controller.signal }
      );
      if (controller.signal.aborted || sequence !== requestRef.current.sequence)
        return;
      const { results, ...rest } = data;
      setData(results);
      setTotalPages(rest.totalPages ?? rest.pageSize ?? 0);
      setCount(rest.total_count ?? rest.total ?? rest.totalCount ?? 0);
      callbacks.current.onSuccess?.(rest);
      setStatus("success");
    } catch (err) {
      if (controller.signal.aborted || sequence !== requestRef.current.sequence)
        return;
      setErrorMessage(err?.response?.data?.msg);
      setStatus("error");
    }
  }, [params, enabled, fetchOnlyOnSearch]);

  useEffect(() => {
    run();
    return () => {
      requestRef.current.sequence += 1;
      requestRef.current.controller?.abort();
    };
  }, [run]);

  return {
    data,
    setData,
    params,
    onChange: debaunceChange,
    changeNow: onChange,
    status,
    count,
    totalPages,
    refresh: run,
    handleSearch: debaunceSearch,
    errorMessage
  };
};
